import { Checkmark, ChevronDown, Layers } from '@carbon/icons-react'
import React, { useEffect, useState } from 'react'

import { Button } from '../../../../components/New/Button/Button'
import { DDSTypography } from 'den-design-system'
import { FiZap } from 'react-icons/fi'
import StringConstants from '../../../../constants/StringConstants'
import { ToastMessageConstants } from '../../../../constants/ToastMessageConstants'
import ToastNotification from '../../../../components/common/DDS/Toast/Toast'
import { getStripeCustomerPortalUrl } from '../../../../api/stripe/stripe.service'
import pallete from '../../../../global/newPallete'
import { replaceUrl } from '../../../../utils/UrlUtils'
import routeConstants from '../../../../constants/RouteConstants'
import subscriptionBgImage from '../../../../assets/icons/subscriptionBgImage.svg'
import { useStoreContext } from '../../../../store/StoreContext'

interface PlanFeature {
  name: string
  basic: string | boolean
  enterprise: string | boolean
}

const PricingComponent: React.FC = () => {
  const [showFullFeatures, setShowFullFeatures] = useState(false)
  const store = useStoreContext()

  const [stripeCustomerPortalUrl, setStripeCustomerPortalUrl] =
    useState<string>()
  const organizationDetails = store.userStore.getOrganizationDetails()

  const { id, stripeCustomerId, subscription } = organizationDetails || {}
  const status = subscription?.status

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: (
          <div className='pointer-events-none cursor-not-allowed'>
            {' '}
            Manage Organization
          </div>
        ),
        link: `${replaceUrl(
          routeConstants.MANAGE_ORGANIZATION,
          store.scopeStore.getScope()['organization-id'] as string
        )}?tab=3`
      },
      {
        label: 'Billing',
        link: `${replaceUrl(
          routeConstants.MANAGE_ORGANIZATION,
          store.scopeStore.getScope()['organization-id'] as string
        )}?tab=3`
      },
      {
        label: 'Subscription Plan',
        link: `${replaceUrl(
          routeConstants.MANAGE_ORGANIZATION,
          store.scopeStore.getScope()['organization-id'] as string
        )}?tab=3`
      }
    ])
  }, [store.breadcrumbStore, store.scopeStore])

  const fetchStripeDetails = () => {
    store.uiStore.setGlobalLoader(true)

    getStripeCustomerPortalUrl({
      $id: stripeCustomerId,
      $organizationId: id
    })
      .then((response: any) => {
        setStripeCustomerPortalUrl(response.data.message)
      })
      .catch((e: any) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.STRIPE
        })
      })
      .finally(() => {
        store.uiStore.setGlobalLoader(false)
      })
  }

  useEffect(() => {
    if (stripeCustomerPortalUrl) {
      window.location.href = stripeCustomerPortalUrl
    }
  }, [stripeCustomerPortalUrl])

  const basicFeatures = [
    'Upto 25,000 Error monitoring',
    '10 scheduled jobs',
    '25,000 logs of API monitoring',
    '10 uptime checks',
    'Once in 36hrs sales force audit'
  ]

  const enterpriseFeatures = [
    'Custom error monitoring',
    'Custom scheduled jobs',
    'Custom API monitoring',
    'Custom uptime checks',
    'Custom sales force audit'
  ]

  const fullFeatures: PlanFeature[] = [
    { name: 'Number of users', basic: 'Unlimited', enterprise: 'Unlimited' },
    { name: 'Projects', basic: 'Unlimited', enterprise: 'Unlimited' },
    {
      name: 'Organization Type',
      basic: 'Full-stack / Salesforce / Mulesoft',
      enterprise: 'Full-stack / Salesforce / Mulesoft'
    },
    { name: 'Error Monitoring', basic: '25,000 / month', enterprise: 'Custom' },
    { name: 'APIs', basic: '25,000 logs / month', enterprise: 'Custom' },
    { name: 'Track API Usage', basic: 'No', enterprise: 'Custom' },
    { name: 'Scheduled Jobs', basic: '10', enterprise: 'Custom' },
    { name: 'Uptime Checks', basic: '10', enterprise: 'Custom' },
    {
      name: 'Salesforce Audit(Frequency)',
      basic: '2 / 4 / 6 hours',
      enterprise: 'Custom'
    },
    { name: 'Incident Checks', basic: '10 / month', enterprise: 'Custom' },
    { name: 'Escalation', basic: 'Unlimited', enterprise: 'Unlimited' },
    { name: 'Notification Integration', basic: 'All', enterprise: 'All' },
    { name: 'Data Retention', basic: '30 days', enterprise: 'Custom' },
    { name: 'Reporting', basic: '-', enterprise: 'API Support' },
    { name: 'Support SLA', basic: '48 - 72hrs', enterprise: 'Less than 12hrs' },
    { name: 'Trial Period', basic: '15 days', enterprise: '15 days' }
  ]

  if (showFullFeatures) {
    return (
      <div className='min-h-screen bg-white p-4'>
        <div className='max-w-6xl mx-auto'>
          <div className='text-center mb-8'>
            <button
              onClick={() => setShowFullFeatures(false)}
              className='inline-flex items-center px-[8px] py-[4px] border border-blue-300 rounded-full text-blue-600 hover:bg-blue-50 transition-colors'
            >
              <DDSTypography.Paragraph
                size='caption'
                variant='semiBold'
                color={pallete.colors.primary2}
              >
                Choose plans
              </DDSTypography.Paragraph>
            </button>
          </div>

          <div className='bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200'>
            <div className='grid grid-cols-3'>
              <div className='p-[12px] bg-white border-r border-gray-200'>
                <DDSTypography.Title
                  type='h2'
                  variant='medium'
                  color={pallete.colors.black}
                >
                  Features
                </DDSTypography.Title>
              </div>
              <div className='p-6 border-r border-gray-200 bg-white'>
                <div
                  className='inline-flex items-center gap-2 px-[8px] py-[8px] rounded-full  mb-[8px]'
                  style={{ backgroundColor: pallete.colors.surface6 }}
                >
                  <FiZap
                    color={pallete.colors.fillButtonhover}
                    className='w-[20px] h-[20px]'
                  />
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.fillButtonhover}
                  >
                    Basic
                  </DDSTypography.Paragraph>
                </div>
                <DDSTypography.Title
                  type='h5'
                  variant='medium'
                  color={pallete.colors.textDark6}
                  className='mb-[12px]'
                >
                  for solo devs
                </DDSTypography.Title>
                <div className='mb-4'>
                  <DDSTypography.Title
                    type='h2'
                    variant='bold'
                    color={pallete.colors.black}
                  >
                    $199/mo
                  </DDSTypography.Title>
                </div>
                {status === 'active' &&
                store.userStore.getPlanType() ===
                  StringConstants.STRIPE.BASIC_PLAN_TYPE ? (
                  <Button
                    id='currentPlan'
                    onClick={() => fetchStripeDetails()}
                    disabled={
                      store.userStore.getPlanType() ===
                      StringConstants.STRIPE.BASIC_PLAN_TYPE
                    }
                    type='outline'
                    label='Current Plan'
                    size='medium'
                  />
                ) : (
                  <Button
                    id='getStarted'
                    onClick={() => fetchStripeDetails()}
                    type='filled'
                    label={'Get Started'}
                    size='medium'
                  />
                )}
              </div>
              <div className='p-6 bg-white'>
                <div
                  className='inline-flex items-center gap-[8px] px-[8px] py-[8px] rounded-full  mb-[8px]'
                  style={{ backgroundColor: pallete.colors.surface6 }}
                >
                  <Layers
                    color={pallete.colors.fillButtonhover}
                    className='w-[20px] h-[20px] text-blue-600'
                  />
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.fillButtonhover}
                  >
                    Enterprise
                  </DDSTypography.Paragraph>
                </div>
                <DDSTypography.Title
                  type='h5'
                  variant='medium'
                  color={pallete.colors.textDark6}
                  className='mb-[12px]'
                >
                  Contact us
                </DDSTypography.Title>
                <div className='mb-4'>
                  <DDSTypography.Title
                    type='h2'
                    variant='bold'
                    color={pallete.colors.black}
                  >
                    {`Let's talk`}
                  </DDSTypography.Title>
                </div>

                {status === 'active' &&
                store.userStore.getPlanType() ===
                  StringConstants.STRIPE.ENTERPRISE_PLAN_TYPE ? (
                  <Button
                    id='currentPlan'
                    disabled={
                      store.userStore.getPlanType() ===
                      StringConstants.STRIPE.ENTERPRISE_PLAN_TYPE
                    }
                    type='outline'
                    label='Current Plan'
                    size='medium'
                  />
                ) : (
                  <Button
                    id='getStarted'
                    onClick={() => {
                      window.location.href =
                        'mailto:support@vigilnow.com?subject= Vigil - Enterprise Plan Inquiry'
                    }}
                    type='filled'
                    label={'Contact Us'}
                    size='medium'
                  />
                )}
              </div>
            </div>

            {fullFeatures.map((feature, index) => (
              <div
                key={index}
                className={`grid grid-cols-3 border-t border-gray-100 `}
                style={{
                  backgroundColor:
                    index % 2 === 0
                      ? pallete.colors.surface5
                      : pallete.colors.white
                }}
              >
                <div className='p-[12px] border-r border-gray-200 font-medium'>
                  <DDSTypography.Title
                    type='h5'
                    variant='medium'
                    color={pallete.colors.black}
                  >
                    {feature.name}
                  </DDSTypography.Title>
                </div>
                <div className='p-[12px]  border-r border-gray-200'>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='regular'
                    color={pallete.colors.textDark4}
                  >
                    {feature.basic}
                  </DDSTypography.Paragraph>
                </div>
                <div className='p-[12px] '>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='regular'
                    color={pallete.colors.textDark4}
                  >
                    {feature.enterprise}
                  </DDSTypography.Paragraph>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className='min-h-screen w-full bg-white p-[8px]'
      style={{
        backgroundImage: `url(${subscriptionBgImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }}
    >
      <div className='max-w-[690px] mx-auto'>
        <div className='text-center mb-[20px]'>
          <button
            onClick={() => setShowFullFeatures(false)}
            className='inline-flex items-center px-[8px] py-[4px] border border-blue-300 rounded-full text-blue-600 hover:bg-blue-50 mb-[12px] transition-colors'
          >
            <DDSTypography.Paragraph
              size='caption'
              variant='semiBold'
              color={pallete.colors.primary2}
            >
              Choose plans
            </DDSTypography.Paragraph>
          </button>

          <DDSTypography.Title
            type='h1'
            variant='bold'
            color={pallete.colors.black}
            className='mb-[6px]'
          >
            Explore the ideal subscription option.
          </DDSTypography.Title>
          <DDSTypography.Paragraph
            size='para'
            variant='regular'
            color={pallete.colors.textDark6}
          >
            Our pricing fits teams of all sizes, so you can find the right match
            for your needs
          </DDSTypography.Paragraph>
        </div>

        <div className='grid md:grid-cols-2 gap-8 max-w-[20px]xl mx-auto'>
          {/* Basic Plan */}
          <div className='bg-white rounded-2xl shadow-lg p-8 border border-gray-200'>
            <div className='text-center mb-8'>
              <div
                className='inline-flex items-center gap-2 px-[8px] py-[8px] rounded-full  mb-[8px]'
                style={{ backgroundColor: pallete.colors.surface6 }}
              >
                <FiZap
                  color={pallete.colors.fillButtonhover}
                  className='w-[20px] h-[20px]'
                />
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.fillButtonhover}
                >
                  Basic
                </DDSTypography.Paragraph>
              </div>

              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                color={pallete.colors.textDark6}
                className='mb-[16px]'
              >
                For solo devs working on small projects
              </DDSTypography.Paragraph>
              <div className='mb-2'>
                <DDSTypography.Title
                  type='h1'
                  variant='bold'
                  color={pallete.colors.black}
                  className='inline'
                >
                  $199/mo
                </DDSTypography.Title>
              </div>

              {status === 'active' &&
              store.userStore.getPlanType() ===
                StringConstants.STRIPE.BASIC_PLAN_TYPE ? (
                <button
                  onClick={() => fetchStripeDetails()}
                  disabled={
                    store.userStore.getPlanType() ===
                    StringConstants.STRIPE.BASIC_PLAN_TYPE
                  }
                  className={`w-full border py-[8px] px-[8px] rounded-lg transition-colors
                    ${
                      store.userStore.getPlanType() ===
                      StringConstants.STRIPE.BASIC_PLAN_TYPE
                        ? 'opacity-60 cursor-not-allowed bg-white'
                        : 'bg-white hover:bg-blue-100 cursor-pointer'
                    }
                  `}
                  style={{ borderColor: pallete.colors.stroke4 }}
                >
                  <DDSTypography.Paragraph
                    size='para'
                    variant='regular'
                    color={pallete.colors.primary2}
                  >
                    Current Plan
                  </DDSTypography.Paragraph>
                </button>
              ) : (
                <button
                  onClick={() => fetchStripeDetails()}
                  style={{ borderColor: pallete.colors.primary2 }}
                  className='w-full bg-blue-600 text-white py-[8px] px-[8px] rounded-lg hover:bg-blue-700 transition-colors'
                >
                  <DDSTypography.Paragraph
                    size='para'
                    variant='regular'
                    color={pallete.colors.white}
                  >
                    Get Started
                  </DDSTypography.Paragraph>
                </button>
              )}
            </div>

            <div className='space-y-[24px]'>
              <div className='text-start'>
                <DDSTypography.Title
                  type='h4'
                  variant='medium'
                  color={pallete.colors.black}
                  className='inline-block'
                >
                  Features you get
                  <span className='text-lg ml-[8px]'>⚡</span>
                </DDSTypography.Title>
              </div>

              <div className='space-y-4'>
                {basicFeatures.map((feature, index) => (
                  <div key={index} className='flex items-start gap-[12px]'>
                    <Checkmark
                      color={pallete.colors.warningGreen}
                      className='w-[20px] h-[20px]  mt-[2px] flex-shrink-0'
                    />
                    <DDSTypography.Paragraph
                      size='para'
                      variant='regular'
                      color={pallete.colors.black}
                    >
                      {feature}
                    </DDSTypography.Paragraph>
                  </div>
                ))}
              </div>

              <div className='border-t border-gray-300 pt-[12px] text-center'>
                <button
                  onClick={() => setShowFullFeatures(true)}
                  className='inline-flex items-center gap-[4px] text-blue-600 hover:text-blue-700 transition-colors bg-transparent border-none cursor-pointer p-[0px]'
                >
                  <DDSTypography.Title
                    type='h4'
                    variant='medium'
                    color={pallete.colors.primary2}
                  >
                    See all features
                  </DDSTypography.Title>
                  <ChevronDown className='w-4 h-4' />
                </button>
              </div>
            </div>
          </div>

          {/* Enterprise Plan */}
          <div className='bg-white rounded-2xl shadow-lg p-[32px] border border-gray-200'>
            <div className='text-center mb-8'>
              <div
                className='inline-flex items-center gap-[8px] px-[8px] py-[8px] rounded-full  mb-[8px]'
                style={{ backgroundColor: pallete.colors.surface6 }}
              >
                <Layers
                  color={pallete.colors.fillButtonhover}
                  className='w-[20px] h-[20px] text-blue-600'
                />
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.fillButtonhover}
                >
                  Enterprise
                </DDSTypography.Paragraph>
              </div>
              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                color={pallete.colors.textDark6}
                className='mb-[16px]'
              >
                Need a custom plan, contact us
              </DDSTypography.Paragraph>
              <div className='mb-[8px]'>
                <DDSTypography.Title
                  type='h1'
                  variant='bold'
                  color={pallete.colors.black}
                  className='inline'
                >
                  {`Let's talk`}
                </DDSTypography.Title>
              </div>

              {store.userStore.getPlanType() ===
                StringConstants.STRIPE.ENTERPRISE_PLAN_TYPE &&
              status === 'active' ? (
                <button
                  onClick={() => fetchStripeDetails()}
                  disabled={
                    store.userStore.getPlanType() ===
                    StringConstants.STRIPE.ENTERPRISE_PLAN_TYPE
                  }
                  className={`w-full border py-[8px] px-[8px] rounded-lg transition-colors
                  ${
                    store.userStore.getPlanType() ===
                    StringConstants.STRIPE.ENTERPRISE_PLAN_TYPE
                      ? 'opacity-50 cursor-not-allowed bg-white'
                      : 'bg-white hover:bg-blue-100 cursor-pointer'
                  }
                `}
                  style={{ borderColor: pallete.colors.primary2 }}
                >
                  <DDSTypography.Paragraph
                    size='para'
                    variant='regular'
                    color={pallete.colors.primary2}
                  >
                    Current Plan
                  </DDSTypography.Paragraph>
                </button>
              ) : (
                <button
                  className='w-full bg-blue-600 text-white py-[8px] px-[8px] rounded-lg hover:bg-blue-700 transition-colors'
                  onClick={() => {
                    window.location.href =
                      'mailto:support@vigilnow.com?subject= Vigil - Enterprise Plan Inquiry'
                  }}
                >
                  <DDSTypography.Paragraph
                    size='para'
                    variant='regular'
                    color={pallete.colors.white}
                  >
                    Contact Us
                  </DDSTypography.Paragraph>
                </button>
              )}
            </div>

            <div className='space-y-6'>
              <div className='text-start'>
                <DDSTypography.Title
                  type='h4'
                  variant='medium'
                  color={pallete.colors.black}
                  className='inline-block'
                >
                  Features you get
                  <span className='text-lg ml-2'>⚡</span>
                </DDSTypography.Title>
              </div>

              <div className='space-y-4'>
                {enterpriseFeatures.map((feature, index) => (
                  <div key={index} className='flex items-start gap-3'>
                    <Checkmark
                      color={pallete.colors.warningGreen}
                      className='w-[20px] h-[20px]  mt-[2px] flex-shrink-0'
                    />
                    <DDSTypography.Paragraph
                      size='para'
                      variant='regular'
                      color={pallete.colors.black}
                    >
                      {feature}
                    </DDSTypography.Paragraph>
                  </div>
                ))}
              </div>

              <div className='border-t border-gray-300 pt-4 text-center'>
                <button
                  onClick={() => setShowFullFeatures(true)}
                  className='inline-flex items-center gap-[4px] text-blue-600 hover:text-blue-700 transition-colors bg-transparent border-none cursor-pointer p-[0px]'
                >
                  <DDSTypography.Title
                    type='h4'
                    variant='medium'
                    color={pallete.colors.primary2}
                  >
                    See all features
                  </DDSTypography.Title>
                  <ChevronDown className='w-4 h-4' />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingComponent
